/**
 * Glitch Effects — runtime processing for glitch side effects.
 * QUAKE_SLAM landing craters live here to avoid circular deps with damage.ts.
 */

import type { GameState } from '../types';
import type { GameEffect } from './effects';
import { GLITCH, PHYSICS } from '../constants';
import { executeCraterPipeline, getGroundY } from '../terrain/grid';
import { unsettleTanksNearCrater } from '../physics/tanks';
import { mirrorSettle } from '../physics/transitions';
import { spawnSmoothingRegion } from '../terrain/sand-physics';
import { SMALL_CRATER } from '../terrain/crater-shapes';
import { lookupTank } from '../tanks';

/**
 * QUAKE_SLAM: create a small crater at each tank's landing position.
 * Called from main.ts after updateTankPhysics when QUAKE_SLAM is active.
 * @param preGrounded - per-player grounded/settled snapshot from before physics
 */
export function processQuakeSlamLandings(
  state: GameState,
  preGrounded: boolean[],
  fx: GameEffect[],
): void {
  for (let i = 0; i < state.players.length; i++) {
    const p = state.players[i];
    if (!p.alive) continue;
    if (preGrounded[i]) continue; // was already grounded
    if (!p.grounded && !p.settled) continue; // still airborne

    // Landing detected — create crater at feet
    const ecb = lookupTank(p.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;
    const craterY = p.y + ecb;
    const result = executeCraterPipeline(p.x, craterY, GLITCH.QUAKE_SLAM_RADIUS, SMALL_CRATER);
    if (result) {
      fx.push({ type: 'terrain_update', bounds: result.finalBounds });
      fx.push({ type: 'terrain_add_crater', x: p.x, y: craterY, radius: GLITCH.QUAKE_SLAM_RADIUS });
      fx.push({ type: 'particles', method: 'spawnRichExplosion', args: [p.x, craterY, 0xffaa00, GLITCH.QUAKE_SLAM_RADIUS, 0.5] });
      fx.push({ type: 'screen_shake', intensity: 4, decay: 0.85 });
      spawnSmoothingRegion(state, result.finalBounds);
      unsettleTanksNearCrater(state.players, p.x, GLITCH.QUAKE_SLAM_RADIUS);

      // Re-settle the landing tank on the new terrain surface so it doesn't
      // fall into its own crater (which would cascade into infinite drilling).
      const newGroundY = getGroundY(p.x);
      p.y = newGroundY - ecb;
      p.grounded = true;
      p.settled = true;
      p.settledX = p.x;
      p.settledY = p.y;
      mirrorSettle(p);
    }

    // Small area damage to nearby tanks
    for (let j = 0; j < state.players.length; j++) {
      if (j === i) continue;
      const other = state.players[j];
      if (!other.alive || other.respawnInvulnFrames > 0) continue;
      const otherEcb = lookupTank(other.tankId).ecbRadius ?? PHYSICS.ECB_RADIUS;
      const dx = other.x - p.x;
      const dy = other.y - craterY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < GLITCH.QUAKE_SLAM_RADIUS + otherEcb) {
        other.damagePercent += GLITCH.QUAKE_SLAM_DAMAGE;
      }
    }
  }
}
